import type { Ledger } from './core/ledger';
import type { SimulateOptions } from './core/simulate';
import type { Plan } from './plan/model';
import { type ResultSet, type RunMonteCarloOptions, runMonteCarlo } from './results';
import type { Rng } from './sampling/rng';
import type { MatrixReturn, ReturnData, ReturnInput, Sampler } from './sampling/sampler';
import { matrixFromDraws, validateReturnData } from './sampling/samplers';

/**
 * SEQUENCE-OF-RETURNS STRESS (M5) — PLAN.md "Layer 2 §2c" sequence-of-returns bullet + the M5
 * milestone. This is the DESIGN agent's published surface; the M5 BUILD agents fill the bodies
 * (every body below throws `"… not implemented (M5 build)"` except thin delegators, so `tsc -b`,
 * Biome, and the M0–M4 suites stay green).
 *
 * THE CORE INVARIANT (PLAN.md / the M5 prompt): stress is a DETERMINISTIC re-ordering / specific-
 * start-year variant of the SAME historical-sequence engine — NOT new randomness. It reuses the
 * existing `Sampler` / `ResultSet` / `simulatePath` machinery wholesale:
 *
 *   - WORST-FIRST / BEST-FIRST orderings SORT the same return set (a single chosen historical
 *     window, or the whole pool) and replay it in that deterministic order. No `Rng` is consumed
 *     for selection (the sort is the whole point), so these are PURE `Sampler`s whose output is a
 *     function of `(data, horizon)` alone.
 *   - `retireIntoYear(history, startYear, horizon)` runs the REAL historical sequence beginning at
 *     a chosen calendar year (the canonical "retire into 2008" question), again a deterministic
 *     contiguous window of the real cross-section — a `Sampler` that produces exactly ONE path.
 *
 * Each stress `Sampler` emits the SAME `matrix` {@link ReturnInput} form the M3 multi-asset
 * samplers do (built through the shared {@link matrixFromDraws} / {@link rowToMatrixYear}), so
 * `simulatePath` allocation-weights it per account UNCHANGED, and the per-path ledger drill-down
 * (`explain` / the failing-path ledger) is preserved intact — the whole point of a stress run is
 * to read WHY it failed, year by year.
 *
 * TAXES are optional via the existing `TaxPolicy` (default `NoTaxPolicy`): a {@link StressOptions}
 * may carry a `taxPolicy` (e.g. a `ThailandTaxPolicy`) exactly like `runMonteCarlo`, so stress
 * works with or without Thai tax.
 *
 * Files: `stress.ts` (this — the stress samplers, `retireIntoYear`, and the scenario comparison);
 * extends `results.ts` (the `ScenarioComparison` shape + the `worstDrawdown` ledger helper) and is
 * re-exported from `index.ts` / `montecarlo.ts`.
 */

// ---------------------------------------------------------------------------
// Canonical stress years (PLAN.md §2c "the Thai canonical stress years")
// ---------------------------------------------------------------------------

/**
 * THE THAI CANONICAL STRESS YEARS (PLAN.md §2c): retire-into-YYYY cohorts a Thai plan must be
 * pressure-tested against. `1997` is the Asian financial crisis / Tom Yum Kung (PLAN.md writes
 * "1996/97"; the calendar-year retire-into anchor is the crisis year 1997, with `1996` available
 * as the eve-of-crisis cohort), then the 2000 dot-com unwind, 2008 GFC, 2013 taper-tantrum +
 * domestic turmoil, and the 2020 COVID shock. These are CALENDAR YEARS matched against a
 * `ReturnData.periods` label (a window that cannot supply a full `horizon` of real rows is
 * skipped / flagged by the comparison, never silently truncated).
 */
export const THAI_CANONICAL_STRESS_YEARS = [1996, 1997, 2000, 2008, 2013, 2020] as const;

/**
 * GLOBAL-SLEEVE CONTEXT STRESS YEARS (PLAN.md §2c "the global sleeve adds 1929/1966/1973 for
 * context"): the deep-history retire-into cohorts (1929 Great Depression, 1966 start of the
 * stagflation decade, 1973 oil shock) that only a long global series (e.g. the `us_*` validation
 * fixture) can reach — the short SET window cannot. Used alongside {@link THAI_CANONICAL_STRESS_YEARS}
 * when the run is on the global sleeve.
 */
export const GLOBAL_CONTEXT_STRESS_YEARS = [1929, 1966, 1973] as const;

// ---------------------------------------------------------------------------
// Deterministic re-ordering samplers (worst-first / best-first)
// ---------------------------------------------------------------------------

/** Direction a {@link ReorderStressSampler} sorts the chosen return set. */
export type StressOrdering = 'worstFirst' | 'bestFirst';

/** Tuning for {@link ReorderStressSampler}. */
export interface ReorderStressOptions {
  /**
   * Sort direction: `'worstFirst'` puts the most damaging years at the START of retirement (the
   * canonical sequence-of-returns nightmare — bad returns while the portfolio is largest and
   * withdrawals bite hardest); `'bestFirst'` is the mirror (a benign sequence, the optimistic
   * bookend).
   */
  readonly ordering: StressOrdering;
  /**
   * WHICH return set is reordered:
   *  - `'pool'` (default): rank EVERY historical row in `data` and take the `horizon` most-extreme
   *    rows (worst or best), then replay them in sorted order — a "what if the worst H years of
   *    all history hit you back-to-back" stress.
   *  - `'window'`: reorder ONLY the contiguous real window starting at `windowStartYear` (the
   *    SAME multiset of returns the retire-into cohort actually saw, just resequenced) — isolates
   *    the SEQUENCE effect while holding the return distribution fixed at one real cohort's.
   */
  readonly scope?: 'pool' | 'window';
  /** Required when `scope: 'window'`: the calendar-year label the reordered window starts at. */
  readonly windowStartYear?: number;
  /**
   * How a single historical ROW is scored for ranking (lower score = "worse"). Default
   * `'equalWeight'` ranks by the equal-weight mean of the row's asset-class returns; `'firstAsset'`
   * ranks by `rows[t][0]` (the lead/equity sleeve). A plan-allocation-aware ranking is a build-time
   * extension; the column order in `data.assetClasses` is the contract either way.
   */
  readonly rankBy?: 'equalWeight' | 'firstAsset';
}

/**
 * WORST-FIRST / BEST-FIRST stress sampler. A PURE, DETERMINISTIC `Sampler` (it IGNORES `rng` — the
 * sort IS the selection) that reorders the SAME real return rows into the requested
 * {@link StressOrdering} and emits them as the `matrix` {@link ReturnInput} form via the shared
 * {@link matrixFromDraws} (so `assetClasses === data.assetClasses` and `simulatePath` weights it
 * per account UNCHANGED). It produces a SINGLE deterministic path (the reordered sequence);
 * `nPaths` is ignored (documented, like `HistoricalSequenceSampler`'s window count).
 *
 * This is exactly "deliberately order the same return set worst-first / best-first" (PLAN.md §2c):
 * same multiset of returns, adversarial sequence — the cleanest way to expose sequence-of-returns
 * risk without introducing any randomness.
 */
export class ReorderStressSampler implements Sampler {
  readonly options: ReorderStressOptions;
  constructor(options: ReorderStressOptions) {
    this.options = options;
  }

  sample(data: ReturnData, horizon: number, _nPaths: number, _rng: Rng): ReturnInput[] {
    // PURE & deterministic: the SORT is the selection — `rng` and `nPaths` are ignored (a single
    // reordered path is produced, exactly like HistoricalSequenceSampler's documented window count).
    validateReturnData(data);
    const rows = data.rows as ReadonlyArray<readonly number[]>;
    const scope = this.options.scope ?? 'pool';

    // 1. Resolve the candidate row indices: the whole historical pool, or only the contiguous real
    //    window starting at `windowStartYear` (same multiset a real cohort saw, just resequenced).
    let candidates: number[];
    if (scope === 'window') {
      const startYear = this.options.windowStartYear;
      if (startYear === undefined) {
        throw new Error("ReorderStressSampler: scope 'window' requires windowStartYear");
      }
      const periods = data.periods;
      if (periods === undefined) {
        throw new Error("ReorderStressSampler: scope 'window' requires data.periods");
      }
      const t0 = periods.indexOf(String(startYear));
      if (t0 < 0) {
        throw new Error(
          `ReorderStressSampler: windowStartYear ${startYear} is not a period label in data`,
        );
      }
      if (t0 + horizon > rows.length) {
        throw new Error(
          `ReorderStressSampler: window at ${startYear} (index ${t0}) cannot supply ${horizon} ` +
            `rows (history ends at index ${rows.length - 1})`,
        );
      }
      candidates = [];
      for (let t = t0; t < t0 + horizon; t++) {
        candidates.push(t);
      }
    } else {
      // 'pool': rank EVERY historical row.
      candidates = rows.map((_row, t) => t);
    }

    // 2. Score each candidate row (lower score = "worse"). Pre-compute so the sort is stable on a
    //    cached score rather than recomputing per comparison.
    const scored = candidates.map((t) => ({
      t,
      score: scoreRow(rows[t] as readonly number[], this.options.rankBy),
    }));

    // 3. Sort by score: ascending for 'worstFirst' (most-damaging first), descending for
    //    'bestFirst'. Ties break by original index so the ordering is fully deterministic.
    const asc = this.options.ordering === 'worstFirst';
    scored.sort((a, b) =>
      a.score === b.score ? a.t - b.t : asc ? a.score - b.score : b.score - a.score,
    );

    // 4. Take the first `horizon` reordered indices. For scope 'window' there are exactly `horizon`
    //    candidates; for 'pool' we keep the `horizon` most-extreme rows back-to-back.
    if (scored.length < horizon) {
      throw new Error(
        `ReorderStressSampler: only ${scored.length} candidate rows for a horizon of ${horizon}`,
      );
    }
    const draws = scored.slice(0, horizon).map((s) => s.t);

    // 5. Build one `matrix` ReturnInput via the shared helper (assetClasses === data.assetClasses).
    return [matrixFromDraws(data, draws)];
  }
}

// ---------------------------------------------------------------------------
// retire-into-YYYY (a real historical sequence starting at a chosen year)
// ---------------------------------------------------------------------------

/**
 * `retireIntoYear(history, startYear, horizon)` — the canonical "retire INTO a specific year"
 * stress. Locates `startYear` in `history.periods` and returns the contiguous real cross-section
 * window `rows[t0 .. t0+horizon-1]` as ONE `matrix` {@link ReturnInput} (the real sequence a
 * retiree who stopped working that year actually lived through). NO randomness — a pure slice of
 * real history; identical to a 1-path `HistoricalSequenceSampler` pinned to one start index.
 *
 * Throws if `startYear` is not a `periods` label, or if the window would run past the end of
 * history (a partial window is never silently truncated — the caller must pick a start year with a
 * full `horizon` of real rows ahead of it, which is what makes the "retire into 2008 / 1997"
 * comparison honest). BUILD (M5) fills the body; it is a thin lookup + `matrixFromDraws`.
 */
export function retireIntoYear(
  history: ReturnData,
  startYear: number,
  horizon: number,
): MatrixReturn {
  validateReturnData(history);
  const rows = history.rows as ReadonlyArray<readonly number[]>;
  const periods = history.periods;
  if (periods === undefined) {
    throw new Error('retireIntoYear: history has no `periods` labels to locate startYear');
  }
  const t0 = periods.indexOf(String(startYear));
  if (t0 < 0) {
    throw new Error(`retireIntoYear: startYear ${startYear} is not a period label in history`);
  }
  // Never silently truncate a partial window — the caller must pick a year with a full `horizon` of
  // real rows ahead, which is what makes the "retire into 2008 / 1997" comparison honest.
  if (t0 + horizon > rows.length) {
    throw new Error(
      `retireIntoYear: window starting at ${startYear} (index ${t0}) needs ${horizon} rows but ` +
        `history ends at index ${rows.length - 1} (only ${rows.length - t0} ahead)`,
    );
  }
  const draws: number[] = [];
  for (let t = t0; t < t0 + horizon; t++) {
    draws.push(t);
  }
  // matrixFromDraws returns a MatrixReturn (kind:'matrix') whose assetClasses === history.assetClasses.
  return matrixFromDraws(history, draws);
}

/**
 * Thin `Sampler` wrapper around {@link retireIntoYear} so a retire-into-YYYY scenario plugs into
 * `runMonteCarlo` / the {@link ScenarioComparison} machinery exactly like any other sampler. PURE
 * and deterministic (ignores `rng`), producing the single real-history path for `startYear`.
 */
export class RetireIntoYearSampler implements Sampler {
  readonly startYear: number;
  constructor(startYear: number) {
    this.startYear = startYear;
  }

  sample(data: ReturnData, horizon: number, _nPaths: number, _rng: Rng): ReturnInput[] {
    // PURE & deterministic: the single real-history window for `startYear`. `rng`/`nPaths` ignored.
    return [retireIntoYear(data, this.startYear, horizon)];
  }
}

// ---------------------------------------------------------------------------
// Per-path drawdown analysis (the "WHY it failed" surface)
// ---------------------------------------------------------------------------

/**
 * The worst peak-to-trough DRAWDOWN of one path's wealth, and WHEN it bottomed — the headline
 * stress metric that turns "it failed" into "it failed because the portfolio fell X% and bottomed
 * in YYYY". Derived purely from a ledger's `endBalance` column (ledger-first invariant).
 */
export interface DrawdownResult {
  /**
   * Maximum drawdown as a NON-NEGATIVE fraction in [0, 1+]: `(peak − trough) / peak` over the
   * running peak. 0 means the balance never fell below a prior peak. (A path that goes negative
   * can exceed 1.)
   */
  readonly maxDrawdown: number;
  /** Calendar-year (`period`) at which the running balance bottomed (the trough year). */
  readonly troughYear: number;
  /** The running-peak balance the trough is measured from. */
  readonly peakBalance: number;
  /** The balance at the trough. */
  readonly troughBalance: number;
  /** Calendar-year of the running peak the worst drawdown ran from. */
  readonly peakYear: number;
}

/**
 * Compute the worst peak-to-trough drawdown of a single path's `endBalance` series, and the year
 * it bottomed. Walks the `endBalance` column once tracking the running peak; the worst drawdown is
 * the largest `(peak − balance) / peak`. BUILD (M5) fills the body (a single linear pass; reads
 * the `period` column for the year labels). Empty / single-row ledger -> zero drawdown.
 */
export function worstDrawdown(ledger: Ledger): DrawdownResult {
  const end = ledger.column('endBalance');
  const periodCol = ledger.column('period');
  const n = ledger.length;
  // Empty / single-row ledger: no peak-to-trough move is possible -> zero drawdown.
  if (n <= 1) {
    const year = n === 1 ? (periodCol[0] as number) : 0;
    const bal = n === 1 ? (end[0] as number) : 0;
    return {
      maxDrawdown: 0,
      troughYear: year,
      peakBalance: bal,
      troughBalance: bal,
      peakYear: year,
    };
  }

  // Single linear pass tracking the running peak and the deepest (peak - balance)/peak below it.
  let runningPeak = end[0] as number;
  let runningPeakYear = periodCol[0] as number;
  let maxDrawdown = 0;
  let troughYear = periodCol[0] as number;
  let peakBalance = end[0] as number;
  let troughBalance = end[0] as number;
  let peakYear = periodCol[0] as number;

  for (let i = 0; i < n; i++) {
    const bal = end[i] as number;
    if (bal > runningPeak) {
      runningPeak = bal;
      runningPeakYear = periodCol[i] as number;
    }
    // Drawdown vs the running peak. Guard a non-positive peak (a portfolio that never had positive
    // wealth) -> 0 so we never divide by zero or report a nonsense fraction.
    const dd = runningPeak > 0 ? (runningPeak - bal) / runningPeak : 0;
    if (dd > maxDrawdown) {
      maxDrawdown = dd;
      troughYear = periodCol[i] as number;
      troughBalance = bal;
      peakBalance = runningPeak;
      peakYear = runningPeakYear;
    }
  }
  return { maxDrawdown, troughYear, peakBalance, troughBalance, peakYear };
}

// ---------------------------------------------------------------------------
// Scenario comparison (baseline vs named stress scenarios)
// ---------------------------------------------------------------------------

/**
 * One named scenario in a {@link ScenarioComparison}: a human label plus the `Sampler` that
 * produces it. The baseline (e.g. a `HistoricalSequenceSampler`) and each stress scenario (a
 * `ReorderStressSampler`, a `RetireIntoYearSampler`, …) are described uniformly so the comparison
 * runs them through the SAME `runMonteCarlo` pipeline.
 */
export interface ScenarioSpec {
  /** Human-readable scenario name, e.g. `'baseline'`, `'worst-first'`, `'retire into 2008'`. */
  readonly name: string;
  /** The sampler producing this scenario's path(s). */
  readonly sampler: Sampler;
}

/**
 * The metrics extracted for ONE scenario, sitting alongside its `ResultSet` so the comparison can
 * be rendered side-by-side. Every number is derived from the scenario's retained path ledgers
 * (ledger-first), and the failing-path ledger is kept for drill-down — the whole point of a stress
 * run is to READ why it failed, year by year.
 */
export interface ScenarioResult {
  /** Echoes {@link ScenarioSpec.name}. */
  readonly name: string;
  /**
   * The FULL `ResultSet` from `runMonteCarlo` for this scenario (ledgers, bands, successProbability,
   * terminalWealth, …) — retained whole so any drill-down (`explainPath`) still works.
   */
  readonly result: ResultSet;
  /** Fraction of this scenario's paths that never ran out of money (== `result.successProbability`). */
  readonly successProbability: number;
  /**
   * Terminal (final-year) wealth: for a single-path stress scenario this is that path's terminal
   * balance; for a multi-path baseline it is the MEDIAN of `result.terminalWealth` (so the
   * side-by-side column is one comparable number). The full sorted vector lives on `result`.
   */
  readonly terminalWealth: number;
  /**
   * The WORST drawdown across this scenario's paths (deepest peak-to-trough of any path) and the
   * year it bottomed — the headline "how bad did it get, and when".
   */
  readonly worstDrawdown: DrawdownResult;
  /**
   * THE FAILING PATH LEDGER (drill-down). The index into `result.ledgers` of the representative
   * path to read year-by-year: the FIRST path that ran out of money if any failed, else the path
   * with the worst drawdown. `undefined` only when there are no paths. Pair with
   * `explainPath(result, failingPathIndex, year)` to read the bank statement of WHY it failed.
   */
  readonly failingPathIndex: number | undefined;
}

/** Options for {@link compareScenarios} — the `runMonteCarlo` inputs shared by every scenario. */
export interface ScenarioComparisonOptions extends StressOptions {
  /**
   * Number of paths requested per scenario (passed straight to each `Sampler.sample`). Deterministic
   * stress samplers (`ReorderStressSampler`, `RetireIntoYearSampler`) ignore it and produce one
   * path; a stochastic baseline (an IID / block bootstrap) honours it.
   */
  readonly nPaths?: number;
}

/** Tax + ordering + data/rng options for a stress run — the stress analogue of
 *  {@link RunMonteCarloOptions}. Taxes are OPTIONAL (default `NoTaxPolicy`): pass a
 *  `ThailandTaxPolicy` to stress WITH Thai tax, omit it to stress without. */
export interface StressOptions extends SimulateOptions {
  /** Historical return data every scenario's sampler draws from (the shared real history). */
  readonly data: ReturnData;
  /**
   * Seeded PRNG. Deterministic stress samplers ignore it; a stochastic baseline consumes it, so a
   * comparison stays reproducible (same seed -> identical comparison). Each scenario is handed the
   * rng in turn, so baseline draws do not perturb the (rng-free) stress scenarios.
   */
  readonly rng: Rng;
  /** Percentiles for each scenario's fan-chart bands (echoed onto each `ResultSet`). */
  readonly percentiles?: readonly number[];
}

/**
 * A side-by-side comparison: the BASELINE scenario against one or more named STRESS scenarios,
 * each with its success / terminal wealth / worst-drawdown-and-its-year and a pointer to the
 * failing-path ledger for drill-down. This is the object the results dashboard's "stress-scenario
 * comparison" (PLAN.md Layer 3 MVP screen 2) and the research log read.
 */
export interface ScenarioComparison {
  /** The reference scenario (no stress) every other scenario is read against. */
  readonly baseline: ScenarioResult;
  /** The named stress scenarios, in the order given. */
  readonly scenarios: readonly ScenarioResult[];
}

/**
 * Build a {@link ScenarioComparison}: run `baseline` and each `stress` scenario through
 * `runMonteCarlo` with the SHARED `options` (same plan, same data, same tax policy / ordering), then
 * extract each scenario's metrics ({@link ScenarioResult}) from its retained ledgers. The whole
 * `ResultSet` (and therefore every path ledger) is kept on each `ScenarioResult` so drill-down
 * survives.
 *
 * REPRODUCIBLE: deterministic stress samplers consume no randomness, so the only nondeterminism is
 * any stochastic baseline's draws from the seeded `rng` — same plan + same data + same seed ->
 * identical comparison. BUILD (M5) fills the body (a thin map over `runScenario`).
 */
export function compareScenarios(
  plan: Plan,
  baseline: ScenarioSpec,
  stress: readonly ScenarioSpec[],
  options: ScenarioComparisonOptions,
): ScenarioComparison {
  const baselineResult = runScenario(plan, baseline, options);
  const scenarios = stress.map((s) => runScenario(plan, s, options));
  return { baseline: baselineResult, scenarios };
}

/**
 * Run ONE {@link ScenarioSpec} and reduce it to a {@link ScenarioResult}: call `runMonteCarlo`
 * with the shared options, then derive successProbability, a single comparable terminal-wealth
 * number (the path's terminal for a 1-path stress, else the median), the worst drawdown across
 * paths, and the failing-path index (first depleted path, else worst-drawdown path). BUILD (M5)
 * fills the body; exposed so a caller can run a single scenario without a full comparison.
 */
export function runScenario(
  plan: Plan,
  spec: ScenarioSpec,
  options: ScenarioComparisonOptions,
): ScenarioResult {
  // Run this scenario through the SAME runMonteCarlo pipeline as any other sampler. Deterministic
  // stress samplers ignore `nPaths`/`rng` and emit one path; a stochastic baseline honours them.
  const mcOptions: RunMonteCarloOptions = {
    data: options.data,
    rng: options.rng,
    ...(options.taxPolicy ? { taxPolicy: options.taxPolicy } : {}),
    ...(options.ordering ? { ordering: options.ordering } : {}),
    ...(options.percentiles ? { percentiles: options.percentiles } : {}),
  };
  const result = runMonteCarlo(plan, spec.sampler, options.nPaths ?? 1, mcOptions);
  const ledgers = result.ledgers;

  // Worst drawdown across all paths (deepest peak-to-trough of any path) + the failing-path index:
  // the FIRST path that ever went negative, else the path with the worst drawdown; undefined iff
  // there are no paths.
  let failingPathIndex: number | undefined;
  let worstAcrossPaths: DrawdownResult | undefined;
  let worstDdPathIndex = -1;
  let firstDepletedIndex = -1;

  for (let i = 0; i < ledgers.length; i++) {
    const ledger = ledgers[i] as Ledger;
    const dd = worstDrawdown(ledger);
    if (worstAcrossPaths === undefined || dd.maxDrawdown > worstAcrossPaths.maxDrawdown) {
      worstAcrossPaths = dd;
      worstDdPathIndex = i;
    }
    if (firstDepletedIndex < 0 && pathDepletes(ledger)) {
      firstDepletedIndex = i;
    }
  }

  if (ledgers.length === 0) {
    failingPathIndex = undefined;
  } else if (firstDepletedIndex >= 0) {
    failingPathIndex = firstDepletedIndex;
  } else {
    failingPathIndex = worstDdPathIndex;
  }

  // One comparable terminal-wealth number: the single path's terminal for a 1-path stress, else the
  // median of the (already ascending) terminalWealth vector. result.terminalWealth is sorted asc.
  const tw = result.terminalWealth;
  const comparableTerminal =
    tw.length === 0
      ? 0
      : tw.length === 1
        ? (tw[0] as number)
        : (tw[Math.floor(tw.length / 2)] as number);

  // No paths -> a zero drawdown placeholder (failingPathIndex is undefined alongside it).
  const dd: DrawdownResult = worstAcrossPaths ?? {
    maxDrawdown: 0,
    troughYear: 0,
    peakBalance: 0,
    troughBalance: 0,
    peakYear: 0,
  };

  return {
    name: spec.name,
    result,
    successProbability: result.successProbability,
    terminalWealth: comparableTerminal,
    worstDrawdown: dd,
    failingPathIndex,
  };
}

/**
 * Convenience: build a {@link ScenarioComparison} of the canonical retire-into-YYYY cohorts against
 * a baseline. For each year in `years` (default {@link THAI_CANONICAL_STRESS_YEARS}) that `data`
 * can supply a full `horizon` for, adds a `RetireIntoYearSampler` scenario named `"retire into
 * YYYY"`; years without enough real history ahead are skipped (the comparison only contains honest,
 * full-length cohorts). BUILD (M5) fills the body on top of {@link compareScenarios}.
 */
export function compareRetireIntoYears(
  plan: Plan,
  baseline: ScenarioSpec,
  options: ScenarioComparisonOptions,
  years: readonly number[] = THAI_CANONICAL_STRESS_YEARS,
): ScenarioComparison {
  const periods = options.data.periods ?? [];
  const rowCount = options.data.rows?.length ?? 0;
  const horizon = plan.horizonYears;
  // Keep only cohorts present in the data WITH a full `horizon` of real rows ahead — never a
  // truncated partial window (so every scenario in the comparison is an honest full-length cohort).
  const eligible = years.filter((y) => {
    const t0 = periods.indexOf(String(y));
    return t0 >= 0 && t0 + horizon <= rowCount;
  });
  const specs: ScenarioSpec[] = eligible.map((y) => ({
    name: `retire into ${y}`,
    sampler: new RetireIntoYearSampler(y),
  }));
  return compareScenarios(plan, baseline, specs, options);
}

// ---------------------------------------------------------------------------
// Internal helpers (private to stress.ts)
// ---------------------------------------------------------------------------

/**
 * Score one historical ROW for the worst-first / best-first ranking (lower score = "worse").
 *  - `'equalWeight'` (default): the equal-weight mean of the row's asset-class returns.
 *  - `'firstAsset'`: `row[0]` (the lead / equity sleeve).
 * The column order in `data.assetClasses` is the contract either way (PLAN.md §2c).
 */
function scoreRow(
  row: readonly number[],
  rankBy: 'equalWeight' | 'firstAsset' | undefined,
): number {
  if (rankBy === 'firstAsset') {
    return row[0] as number;
  }
  // equalWeight (default): the arithmetic mean of the row's per-asset returns.
  if (row.length === 0) {
    return 0;
  }
  let sum = 0;
  for (const v of row) {
    sum += v;
  }
  return sum / row.length;
}

/** Whether a path ran out of money — the same "fails" definition as `successProbability`.
 *  ENRICHMENT (floor-at-zero): a depleted year is now flagged via the `depleted` ledger column
 *  (the balance floors at 0 instead of going negative), with the legacy `endBalance < 0` kept as a
 *  defensive fallback for hand-built ledgers. */
function pathDepletes(ledger: Ledger): boolean {
  const end = ledger.column('endBalance');
  const hasDepleted = ledger.hasColumn('depleted');
  const depletedCol = hasDepleted ? ledger.column('depleted') : undefined;
  for (let i = 0; i < ledger.length; i++) {
    if (depletedCol !== undefined && (depletedCol[i] as number) > 0.5) {
      return true;
    }
    if ((end[i] as number) < 0) {
      return true;
    }
  }
  return false;
}
